import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeHtml, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags } from './helpers';

const ALL_FEATURES = [
  'about-section', 'services-list', 'testimonials', 'contact-form',
  'phone-display', 'address-map', 'business-hours', 'social-links',
  'faq', 'price-list', 'promotions', 'photo-gallery', 'google-reviews',
];

const WAVE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" class="wave-divider"><path fill="currentColor" d="M0,40 C360,100 720,0 1080,60 C1260,80 1380,40 1440,40 L1440,100 L0,100 Z"/></svg>`;

const PASTEL_COLORS = ['#fce7f3', '#e0f2fe', '#f3e8ff', '#fef9c3', '#d1fae5', '#ffe4e6'];

export const creativePlayful: TemplateRegistration = {
  meta: {
    id: 'creative-playful',
    name: 'Creative Playful',
    category: 'Creative',
    description: 'Fun and colorful design with playful shapes, speech bubbles, wave dividers, and bouncy animations',
    previewImage: '/previews/creative-playful.png',
    supportedFeatures: ALL_FEATURES,
    defaultEnabledFeatures: [
      'about-section', 'services-list', 'testimonials', 'contact-form',
      'phone-display', 'address-map', 'business-hours', 'social-links',
    ],
    themeEditable: false,
  },

  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const b = data.businessName;
    const bSafe = sanitizeText(b);
    const headline = sanitizeText(data.headline);
    const aboutText = sanitizeText(data.aboutText);
    const seoTitle = sanitizeSeoText(data.seo?.title || b, 70);
    const seoDesc = sanitizeSeoText(data.seo?.description || data.headline, 160);
    const fontsUrl = buildFontsUrl(['Baloo 2', 'Nunito']);

    const phone = data.businessData?.phone || '';
    const contactEmail = data.businessData?.contactEmail || null;
    const address = data.businessData?.address || '';
    const rating = data.businessData?.rating || 0;
    const reviewCount = data.businessData?.reviewCount || 0;
    const hours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const mapsUrl = data.businessData?.googleMapsUrl || '';

    const galleryPhotos = photos.slice(1, 8);
    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryPhotos.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);

    // Split headline into words and assign colors
    const colors = ['#ec4899', '#06b6d4', '#8b5cf6', '#eab308'];
    const headlineWords = headline.split(' ');
    const coloredHeadline = headlineWords.map((w, i) =>
      `<span style="color:${colors[i % colors.length]}">${w}</span>`
    ).join(' ');

    const navHtml = navLinks.map(l =>
      `<a href="${escapeAttr(l.href)}" class="nav-link">${escapeHtml(l.label)}</a>`
    ).join('');

    const promotionHtml = enabledFeatures.has('promotions') && data.promotion ? `
      <div class="promo-banner">
        <span class="promo-emoji">&#127881;</span>
        <span class="promo-discount">${sanitizeText(data.promotion.discount)}</span>
        <span class="promo-title">${sanitizeText(data.promotion.title)}</span>
        &mdash; ${sanitizeText(data.promotion.description)}
      </div>` : '';

    const heroHtml = `
      <section class="hero">
        <div class="hero-card-stack">
          <div class="hero-card-bg-2"></div>
          <div class="hero-card-bg-1"></div>
          <div class="hero-card">
            <h1 class="hero-headline">${coloredHeadline}</h1>
            <p class="hero-sub">${bSafe}</p>
            ${enabledFeatures.has('phone-display') && phone ? `<p class="hero-phone">${getIconSvg('phone')} ${sanitizeText(phone)}</p>` : ''}
            ${enabledFeatures.has('google-reviews') && rating > 0 ? `<div class="hero-rating">${renderStars(rating)} <span class="rating-text">${rating.toFixed(1)} (${reviewCount} reviews)</span></div>` : ''}
            <a href="#contact" class="hero-cta">Get Started</a>
          </div>
        </div>
      </section>`;

    const aboutHtml = enabledFeatures.has('about-section') ? `
      <div class="wave-section" style="color:#f3e8ff;">${WAVE_SVG}</div>
      <section id="about" class="section about-section" style="background:#f3e8ff;">
        <h2 class="section-title">About Us</h2>
        <p class="section-subtitle">Here's our story</p>
        <div class="about-decorated">
          <div class="about-ornament"><span></span><span></span><span></span></div>
          <p>${aboutText}</p>
          <div class="about-ornament"><span></span><span></span><span></span></div>
        </div>
      </section>
      <div class="wave-section wave-flip" style="color:#f3e8ff;">${WAVE_SVG}</div>` : '';

    const servicesHtml = enabledFeatures.has('services-list') && data.services.length > 0 ? `
      <section id="services" class="section">
        <h2 class="section-title">What We Do</h2>
        <p class="section-subtitle">Our awesome services</p>
        <div class="services-grid">
          ${data.services.map((s, i) => `
            <div class="service-card" style="background:${PASTEL_COLORS[i % PASTEL_COLORS.length]};">
              <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
              <h3 class="service-name">${sanitizeText(s.name)}</h3>
              <p class="service-desc">${sanitizeText(s.description)}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          `).join('')}
        </div>
      </section>` : '';

    const testimonialsHtml = enabledFeatures.has('testimonials') && data.testimonials.length > 0 ? `
      <section id="testimonials" class="section">
        <h2 class="section-title">Happy Customers</h2>
        <p class="section-subtitle">Don't just take our word for it</p>
        <div class="testimonials-stack">
          ${data.testimonials.map((t, i) => `
            <div class="testimonial-block" style="--bubble-color:${PASTEL_COLORS[i % PASTEL_COLORS.length]};background:var(--bubble-color);">
              <p class="bubble-text">${sanitizeText(t.content)}</p>
              ${t.rating ? `<div class="bubble-stars">${renderStars(t.rating)}</div>` : ''}
              <div class="bubble-author">
                <div class="author-avatar" style="background:${colors[i % colors.length]};">${sanitizeText(t.name).charAt(0)}</div>
                <div>
                  <span class="author-name">${sanitizeText(t.name)}</span>
                  ${t.role ? `<span class="author-role">${sanitizeText(t.role)}</span>` : ''}
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      </section>` : '';

    const galleryHtml = enabledFeatures.has('photo-gallery') && photos.length > 1 ? `
      <section id="gallery" class="section">
        <h2 class="section-title">Gallery</h2>
        <p class="section-subtitle">Take a peek!</p>
        <div class="gallery-grid">
          ${photos.slice(0, 8).map((p, i) => `
            <div class="gallery-item" style="transform:rotate(${(i % 2 === 0 ? -2 : 2)}deg);">
              <img src="${escapeAttr(p)}" alt="${escapeAttr(b)} photo" loading="lazy" />
            </div>
          `).join('')}
        </div>
      </section>` : '';

    const faqHtml = enabledFeatures.has('faq') && data.faq && data.faq.length > 0 ? `
      <div class="wave-section" style="color:#e0f2fe;">${WAVE_SVG}</div>
      <section id="faq" class="section" style="background:#e0f2fe;">
        <h2 class="section-title">Got Questions?</h2>
        <p class="section-subtitle">We've got answers!</p>
        <div class="faq-list">
          ${data.faq.map(f => `
            <details class="faq-item">
              <summary class="faq-question">${sanitizeText(f.question)}</summary>
              <p class="faq-answer">${sanitizeText(f.answer)}</p>
            </details>
          `).join('')}
        </div>
      </section>
      <div class="wave-section wave-flip" style="color:#e0f2fe;">${WAVE_SVG}</div>` : '';

    const priceHtml = enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0 ? `
      <section id="pricing" class="section">
        <h2 class="section-title">Pricing</h2>
        <p class="section-subtitle">Transparent and fair</p>
        <div class="price-grid">
          ${data.priceList.map((p, i) => `
            <div class="price-card" style="border-top: 4px solid ${colors[i % colors.length]};">
              <span class="price-amount">${sanitizeText(p.price)}</span>
              <h3 class="price-name">${sanitizeText(p.name)}</h3>
              <p class="price-desc">${sanitizeText(p.description)}</p>
            </div>
          `).join('')}
        </div>
      </section>` : '';

    const hoursHtml = enabledFeatures.has('business-hours') && hours.length > 0 ? `
      <section class="section hours-section">
        <h2 class="section-title">Business Hours</h2>
        <div class="hours-grid">
          ${hours.map((h, i) => `
            <div class="hours-chip" style="background:${PASTEL_COLORS[i % PASTEL_COLORS.length]};">
              ${sanitizeText(h)}
            </div>
          `).join('')}
        </div>
      </section>` : '';

    const contactHtml = enabledFeatures.has('contact-form') ? `
      <section id="contact" class="section">
        <h2 class="section-title">Say Hello!</h2>
        <p class="section-subtitle">We'd love to hear from you</p>
        <div class="contact-info-row">
          ${enabledFeatures.has('phone-display') && phone ? `
          <div class="contact-chip">
            <span class="chip-icon">${getIconSvg('phone')}</span>
            <span>${sanitizeText(phone)}</span>
          </div>` : ''}
          ${enabledFeatures.has('address-map') && address ? `
          <div class="contact-chip">
            <span class="chip-icon">${getIconSvg('map-pin')}</span>
            <span>${sanitizeText(address)}</span>
          </div>` : ''}
          ${enabledFeatures.has('address-map') && mapsUrl ? `<a href="${escapeAttr(mapsUrl)}" target="_blank" rel="noopener" class="map-link">Open in Maps</a>` : ''}
        </div>
        <form class="contact-form inline-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
          <input type="text" placeholder="Name" class="form-input" required />
          <input type="email" placeholder="Email" class="form-input" required />
          <textarea placeholder="Message" class="form-textarea" rows="1" required></textarea>
          <button type="submit" class="form-btn">Send It!</button>
        </form>
      </section>` : '';

    const footerHtml = `
      <footer class="site-footer">
        <div class="footer-inner">
          <p class="footer-brand">${bSafe}</p>
          ${enabledFeatures.has('social-links') ? `
          <div class="social-links">
            <a href="#" class="social-link social-fb" aria-label="Facebook">Fb</a>
            <a href="#" class="social-link social-tw" aria-label="Twitter">Tw</a>
            <a href="#" class="social-link social-ig" aria-label="Instagram">Ig</a>
          </div>` : ''}
          <p class="footer-copy">${bSafe}</p>
        </div>
      </footer>`;

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${seoTitle}</title>
  <meta name="description" content="${seoDesc}" />
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="${fontsUrl}" rel="stylesheet" />
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

    :root {
      --pink: #ec4899;
      --cyan: #06b6d4;
      --purple: #8b5cf6;
      --yellow: #eab308;
      --text: #1e293b;
      --muted: #64748b;
      --bg: #ffffff;
      --heading-font: 'Baloo 2', 'Trebuchet MS', sans-serif;
      --body-font: 'Nunito', 'Segoe UI', sans-serif;
      --radius: 20px;
    }

    html { scroll-behavior: smooth; }

    body {
      font-family: var(--body-font);
      color: var(--text);
      background: var(--bg);
      line-height: 1.7;
      font-size: 16px;
      -webkit-font-smoothing: antialiased;
      overflow-x: hidden;
    }

    /* NAV */
    .site-nav {
      position: sticky; top: 0; z-index: 100;
      background: rgba(255,255,255,0.9);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-bottom: 2px solid #f1f5f9;
      padding: 0 24px;
    }
    .nav-inner {
      max-width: 1100px; margin: 0 auto;
      display: flex; align-items: center; justify-content: space-between;
      height: 60px;
    }
    .nav-brand {
      font-family: var(--heading-font);
      font-size: 1.4rem; font-weight: 800;
      background: linear-gradient(135deg, var(--pink), var(--purple), var(--cyan));
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      background-clip: text;
      text-decoration: none;
    }
    .nav-links { display: flex; gap: 20px; }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--purple); margin: 5px 0; border-radius: 2px; }
    .nav-link {
      font-family: var(--body-font); font-size: 0.9rem;
      color: var(--muted); text-decoration: none;
      font-weight: 600; border-radius: 12px;
      padding: 4px 12px; transition: all 0.2s;
    }
    .nav-link:hover {
      background: #f3e8ff; color: var(--purple);
    }

    /* PROMO */
    .promo-banner {
      background: linear-gradient(135deg, var(--pink), var(--purple));
      color: #fff; text-align: center; padding: 10px 24px;
      font-size: 0.9rem; font-weight: 600;
    }
    .promo-emoji { margin-right: 6px; }
    .promo-discount { font-weight: 800; margin-right: 4px; }

    /* HERO - card stack with overlapping rotated cards */
    .hero {
      position: relative; overflow: hidden;
      max-width: 1100px; margin: 0 auto; padding: 100px 24px 60px;
      display: flex; align-items: center; justify-content: center; min-height: 90vh;
    }
    .hero-card-stack {
      position: relative; width: 100%; max-width: 600px; margin: 0 auto;
    }
    .hero-card {
      background: #fff; border-radius: var(--radius); padding: 48px 40px;
      box-shadow: 0 12px 40px rgba(0,0,0,0.1);
      position: relative;
    }
    .hero-card-bg-1 {
      position: absolute; top: 12px; left: -16px; right: -16px; bottom: -12px;
      background: #fce7f3; border-radius: var(--radius);
      transform: rotate(-3deg); z-index: -1;
    }
    .hero-card-bg-2 {
      position: absolute; top: 20px; left: -24px; right: -24px; bottom: -20px;
      background: #e0f2fe; border-radius: var(--radius);
      transform: rotate(2deg); z-index: -2;
    }
    .hero-headline {
      font-family: var(--heading-font);
      font-size: 2.6rem; font-weight: 800; line-height: 1.2; text-align: center;
    }
    .hero-sub {
      font-size: 1.1rem; color: var(--muted); margin-top: 12px; text-align: center;
      font-weight: 600;
    }
    .hero-phone {
      margin-top: 12px; display: flex; align-items: center; justify-content: center; gap: 8px;
      color: var(--muted); font-size: 0.95rem;
    }
    .hero-phone .icon-char { font-size: 18px; line-height: 1; }
    .hero-rating {
      margin-top: 10px; display: flex; align-items: center; justify-content: center; gap: 8px;
    }
    .star.filled { color: var(--yellow); }
    .star { color: #d4d0c8; }
    .rating-text { font-size: 0.85rem; color: var(--muted); font-weight: 600; }
    .hero-cta {
      display: block; margin: 24px auto 0; width: fit-content;
      padding: 14px 36px;
      background: linear-gradient(135deg, var(--pink), var(--purple));
      color: #fff; text-decoration: none;
      font-family: var(--heading-font); font-weight: 700;
      font-size: 1.05rem; border-radius: 50px;
      transition: transform 0.2s, box-shadow 0.2s;
      box-shadow: 0 4px 20px rgba(139,92,246,0.3);
    }
    .hero-cta:hover {
      transform: translateY(-2px) scale(1.03);
      box-shadow: 0 8px 30px rgba(139,92,246,0.4);
    }

    /* WAVE DIVIDER */
    .wave-section {
      line-height: 0; margin-top: -1px;
    }
    .wave-divider { width: 100%; height: 60px; display: block; }
    .wave-flip { transform: scaleY(-1); margin-bottom: -1px; margin-top: 0; }

    /* SECTIONS */
    .section {
      max-width: 1100px; margin: 0 auto; padding: 48px 24px;
    }
    .section-title {
      font-family: var(--heading-font);
      font-size: 2.2rem; font-weight: 800; text-align: center;
    }
    .section-subtitle {
      text-align: center; color: var(--muted); font-size: 1rem;
      margin-top: 4px; margin-bottom: 32px; font-weight: 600;
    }

    /* ABOUT - centered with decorative borders */
    .about-section { padding-top: 32px; padding-bottom: 32px; }
    .about-decorated {
      max-width: 700px; margin: 0 auto; text-align: center;
      font-size: 1.05rem; line-height: 1.8; padding: 0 24px;
    }
    .about-ornament { display: flex; align-items: center; justify-content: center; gap: 12px; margin: 24px 0; }
    .about-ornament span:nth-child(1), .about-ornament span:nth-child(3) { width: 60px; height: 2px; background: linear-gradient(135deg, var(--pink), var(--purple)); border-radius: 2px; }
    .about-ornament span:nth-child(2) { width: 10px; height: 10px; background: var(--purple); border-radius: 50%; }

    /* SERVICES - masonry columns */
    .services-grid {
      columns: 2; column-gap: 20px;
    }
    .service-card {
      padding: 28px; border-radius: var(--radius);
      transition: transform 0.25s, box-shadow 0.25s;
      cursor: default; break-inside: avoid; margin-bottom: 20px;
      display: inline-block; width: 100%;
    }
    .service-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 12px 32px rgba(0,0,0,0.1);
    }
    .service-icon { color: var(--purple); margin-bottom: 12px; }
    .service-icon .icon-char { font-size: 32px; line-height: 1; }
    .service-name {
      font-family: var(--heading-font);
      font-size: 1.25rem; font-weight: 700; margin-bottom: 6px;
    }
    .service-desc { color: var(--muted); font-size: 0.9rem; }
    .service-price {
      display: inline-block; margin-top: 10px;
      font-weight: 700; color: var(--purple); font-size: 0.95rem;
    }

    /* TESTIMONIALS - stacked alternating left/right */
    .testimonials-stack {
      display: flex; flex-direction: column; gap: 24px; max-width: 700px; margin: 0 auto;
    }
    .testimonial-block {
      background: var(--bubble-color, #fce7f3);
      border-radius: var(--radius);
      padding: 24px 28px;
      max-width: 80%;
      transition: transform 0.2s;
    }
    .testimonial-block:hover { transform: translateY(-4px); }
    .testimonial-block:nth-child(odd) { align-self: flex-start; }
    .testimonial-block:nth-child(even) { align-self: flex-end; }
    .bubble-text { font-size: 0.95rem; line-height: 1.7; font-style: italic; }
    .bubble-stars { margin-top: 8px; }
    .bubble-author {
      margin-top: 12px; display: flex; align-items: center; gap: 10px;
    }
    .author-avatar {
      width: 40px; height: 40px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      color: #fff; font-family: var(--heading-font);
      font-weight: 700; font-size: 1.1rem;
    }
    .author-name {
      font-weight: 700; font-size: 0.9rem; display: block;
    }
    .author-role {
      font-size: 0.8rem; color: var(--muted); display: block;
    }

    /* GALLERY */
    .gallery-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 16px;
    }
    .gallery-item {
      transition: transform 0.3s;
    }
    .gallery-item:hover { transform: rotate(0deg) scale(1.05) !important; }
    .gallery-item img {
      width: 100%; aspect-ratio: 1; object-fit: cover;
      border-radius: var(--radius);
      box-shadow: 0 4px 16px rgba(0,0,0,0.08);
    }

    /* FAQ */
    .faq-list {
      max-width: 700px; margin: 0 auto;
    }
    .faq-item {
      margin-bottom: 10px; background: #fff;
      border-radius: 16px; overflow: hidden;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }
    .faq-question {
      padding: 16px 20px; cursor: pointer;
      font-family: var(--heading-font);
      font-size: 1.05rem; font-weight: 700;
      list-style: none; display: flex; align-items: center; gap: 10px;
    }
    .faq-question::-webkit-details-marker { display: none; }
    .faq-question::before {
      content: '+';
      display: flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; border-radius: 50%;
      background: var(--purple); color: #fff;
      font-size: 1.2rem; font-weight: 400;
      flex-shrink: 0;
    }
    details[open] .faq-question::before { content: '\\2212'; }
    .faq-answer {
      padding: 0 20px 16px 58px;
      color: var(--muted); font-size: 0.95rem; line-height: 1.7;
    }

    /* PRICING */
    .price-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
      gap: 20px;
    }
    .price-card {
      background: #fff; border-radius: var(--radius);
      padding: 28px; text-align: center;
      box-shadow: 0 4px 20px rgba(0,0,0,0.06);
      transition: transform 0.25s;
    }
    .price-card:hover { transform: translateY(-6px); }
    .price-amount {
      font-family: var(--heading-font);
      font-size: 2rem; font-weight: 800; color: var(--purple);
      display: block; margin-bottom: 8px;
    }
    .price-name {
      font-family: var(--heading-font);
      font-size: 1.15rem; font-weight: 700; margin-bottom: 8px;
    }
    .price-desc { color: var(--muted); font-size: 0.9rem; }

    /* HOURS */
    .hours-grid {
      display: flex; flex-wrap: wrap; gap: 10px;
      justify-content: center; margin-top: 24px;
    }
    .hours-chip {
      padding: 10px 20px; border-radius: 50px;
      font-size: 0.9rem; font-weight: 600;
    }

    /* CONTACT - minimal single-line inline form */
    .contact-info-row {
      display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; margin-bottom: 24px;
    }
    .contact-chip {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 18px; background: #f8fafc;
      border-radius: 50px; font-size: 0.9rem;
    }
    .chip-icon { color: var(--purple); }
    .chip-icon .icon-char { font-size: 20px; line-height: 1; }
    .map-link {
      display: inline-block; margin-top: 8px;
      color: var(--cyan); font-weight: 600; font-size: 0.9rem;
      text-decoration: none;
    }
    .map-link:hover { text-decoration: underline; }
    .inline-form {
      display: flex; gap: 10px; max-width: 900px; margin: 0 auto; flex-wrap: wrap; align-items: stretch;
    }
    .form-input, .form-textarea {
      padding: 12px 18px;
      border: 2px solid #e2e8f0; border-radius: 14px;
      font-family: var(--body-font); font-size: 0.95rem;
      color: var(--text); transition: border-color 0.2s; flex: 1; min-width: 140px;
    }
    .form-input:focus, .form-textarea:focus {
      outline: none; border-color: var(--purple);
    }
    .form-textarea { resize: none; min-width: 200px; }
    .form-btn {
      padding: 14px 28px;
      background: linear-gradient(135deg, var(--cyan), var(--purple));
      color: #fff; border: none;
      font-family: var(--heading-font); font-size: 1rem;
      font-weight: 700; cursor: pointer; border-radius: 50px;
      transition: transform 0.2s, box-shadow 0.2s;
      box-shadow: 0 4px 16px rgba(6,182,212,0.3);
      white-space: nowrap;
    }
    .form-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(6,182,212,0.4);
    }

    /* FOOTER */
    .site-footer {
      background: #f8fafc; padding: 40px 24px;
      text-align: center; margin-top: 32px;
    }
    .footer-inner { max-width: 1100px; margin: 0 auto; }
    .footer-brand {
      font-family: var(--heading-font);
      font-size: 1.3rem; font-weight: 800;
      background: linear-gradient(135deg, var(--pink), var(--purple));
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 12px;
    }
    .social-links {
      display: flex; justify-content: center; gap: 12px;
      margin-bottom: 12px;
    }
    .social-link {
      width: 40px; height: 40px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      color: #fff; font-weight: 800; font-size: 0.8rem;
      text-decoration: none; transition: transform 0.2s;
    }
    .social-link:hover { transform: scale(1.15); }
    .social-fb { background: var(--pink); }
    .social-tw { background: var(--cyan); }
    .social-ig { background: var(--purple); }
    .footer-copy { font-size: 0.8rem; color: var(--muted); }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(255,255,255,.98); padding: 16px 24px; gap: 16px; border-bottom: 2px solid #f1f5f9; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .hero { min-height: 70vh; padding-top: 80px; }
      .hero-headline { font-size: 2rem; }
      .hero-card-bg-1, .hero-card-bg-2 { display: none; }
      .services-grid { columns: 1; }
      .testimonial-block { max-width: 100%; }
      .inline-form { flex-direction: column; }
      .price-grid { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 480px) {
      body { font-size: 15px; }
      .hero-headline { font-size: 1.7rem; }
      .section-title { font-size: 1.7rem; }
      .nav-links { gap: 6px; flex-wrap: wrap; }
      .gallery-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
    }
  </style>
</head>
<body>
  <nav class="site-nav">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${bSafe}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <div class="nav-links">${navHtml}</div>
    </div>
  </nav>
  ${promotionHtml}
  ${heroHtml}
  ${aboutHtml}
  ${servicesHtml}
  ${galleryHtml}
  ${testimonialsHtml}
  ${faqHtml}
  ${priceHtml}
  ${hoursHtml}
  ${contactHtml}
  ${footerHtml}
</body>
</html>`;
  },
};
